import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useStyleGuideContext } from '~/grid/context/StyleGuideContext';
import {
  getAutotagValue,
  getFilterPillRBKey,
} from '~/grid/utils/styleGuideUtils';
import {
  Button,
  mobile,
  desktop,
  useProductFiltersCustomFilters,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const StyledButton = styled(Button)`
  height: 30px;
  border-radius: 1em;
  font-size: 14px;
  padding: 8px 16px;
  background-color: ${({ theme, pillSelected }) =>
    pillSelected
      ? theme.colors.palette.black
      : theme.colors.filterPillUnselected};
  border: 1px solid ${({ theme }) => theme.colors.faintBorder};

  & span {
    color: ${({ theme, pillSelected }) =>
      pillSelected ? theme.colors.palette.white : theme.colors.palette.black};
  }

  ${desktop`
    &:hover {
     background-color: ${({ theme }) => theme.colors.palette.black};

     & span {
      color: ${({ theme }) => theme.colors.palette.white}
     }
    }
  `}

  ${mobile`
    &:hover {
     background-color: ${({ theme, pillSelected }) =>
       pillSelected
         ? theme.colors.palette.black
         : theme.colors.filterPillUnselected};
    }
  `}
`;

const DynamicFormattedMessage = FormattedMessage;

export const FilterPill = ({ filterLabel, tagList }) => {
  const { setSelectedFilters, appliedFilters } =
    useProductFiltersCustomFilters();
  const { activeCategoryLabel } = useStyleGuideContext();

  // build RB key for displaying FormattedMessage text for pill
  const rbKey = getFilterPillRBKey({ filterLabel, tagList });

  const autotagValue = getAutotagValue({
    isPill: true,
    tagName: filterLabel,
    activeCategoryLabel,
  });

  // determine if filter is already selected
  const pillSelected =
    appliedFilters?.aggregationFilter?.[tagList]?.includes(filterLabel);

  const handleFilterPillClick = () => {
    const appliedStyleFilters = appliedFilters.aggregationFilter?.[tagList];

    // filter is not currently selected, set filter
    if (!pillSelected) {
      setSelectedFilters((prevSelectedFilters) => ({
        ...prevSelectedFilters,
        aggregationFilter: {
          ...prevSelectedFilters.aggregationFilter,
          [tagList]: [
            filterLabel,
            ...(prevSelectedFilters.aggregationFilter?.[tagList] || []),
          ],
        },
      }));
    } else {
      // filter is currently selected, deselect filter
      const newlyAppliedStyleFilters = appliedStyleFilters?.filter(
        (selectedStyle) => selectedStyle !== filterLabel
      );
      setSelectedFilters((prevSelectedFilters) => ({
        ...prevSelectedFilters,
        aggregationFilter: {
          ...prevSelectedFilters.aggregationFilter,
          [tagList]: newlyAppliedStyleFilters,
        },
      }));
    }
  };

  if (!rbKey) {
    return null;
  }

  return (
    <StyledButton
      onClick={handleFilterPillClick}
      pillSelected={pillSelected}
      data-testid={`filter-pill-${rbKey}`}
      data-autotag={autotagValue}
    >
      <DynamicFormattedMessage
        id={`site_product_filter.${rbKey}`}
        defaultMessage={rbKey}
      />
    </StyledButton>
  );
};

FilterPill.propTypes = {
  filterLabel: PropTypes.string,
  tagList: PropTypes.string,
};

export default FilterPill;
